/**
 * Estimated-cost helpers.
 *
 * Cost is derived (not stored): a project's t-shirt size names a bucket
 * of hours, and the blended hourly rate (from app_settings.cost_blended_rate)
 * multiplies to produce a range. Changing the rate in Settings updates
 * every displayed cost on the next render — no per-project re-save.
 *
 * Buckets mirror EditProjectDialog's TSHIRT_OPTIONS exactly. Keep them
 * here as the single source of truth so the picker, the cost helper,
 * and any future Portfolio column read the same boundaries.
 */

export interface TshirtBucket {
  /** Lower bound (inclusive). 0 for the open-ended "less than" bucket. */
  minHours: number;
  /** Upper bound (exclusive). null for the open-ended "more than" bucket. */
  maxHours: number | null;
}

const BUCKETS: Record<string, TshirtBucket> = {
  "S: < 80 Hours":        { minHours: 0,   maxHours: 80   },
  "M: 80-160 Hours":      { minHours: 80,  maxHours: 160  },
  "L: 160-320 Hours":     { minHours: 160, maxHours: 320  },
  "XL: 320-640 Hours":    { minHours: 320, maxHours: 640  },
  "XXL: > 640 Hours":     { minHours: 640, maxHours: null },
};

/** Return the hour range for a t-shirt label, or null if unrecognized. */
export function tshirtBucket(label: string | null | undefined): TshirtBucket | null {
  if (!label) return null;
  return BUCKETS[label] ?? null;
}

export function fmtMoney(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}m`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${n.toLocaleString()}`;
}

/**
 * Format the cost range for a project.
 *
 *   "S: < 80 Hours" @ $65 → "< $5.2k"
 *   "M: 80-160 Hours" @ $65 → "$5.2k – $10.4k"
 *   "XXL: > 640 Hours" @ $65 → "> $41.6k"
 *   unknown / null tshirt → "—"
 */
export function costRangeLabel(
  tshirt: string | null | undefined,
  rate: number,
): string {
  const b = tshirtBucket(tshirt);
  if (!b || !rate || rate <= 0) return "—";
  const lo = b.minHours * rate;
  const hi = b.maxHours == null ? null : b.maxHours * rate;
  if (b.minHours === 0 && hi != null) return `< ${fmtMoney(hi)}`;
  if (hi == null) return `> ${fmtMoney(lo)}`;
  return `${fmtMoney(lo)} – ${fmtMoney(hi)}`;
}

/**
 * Read the blended rate from an app_settings array. Returns the
 * configured value, or a sane fallback (65) if the setting isn't loaded
 * yet or isn't parseable. Centralized so every consumer agrees on the
 * fallback rather than each picking their own.
 */
export function readBlendedRate(
  settings: Array<{ key: string; value: string }> | undefined,
): number {
  const fallback = 65;
  const row = settings?.find((s) => s.key === "cost_blended_rate");
  const n = row ? parseFloat(row.value) : NaN;
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** Exact dollars for a point estimate ("$13,000"), unlike fmtMoney's
 *  rounded range labels ("$10k"). */
export function fmtMoneyExact(n: number): string {
  return `$${Math.round(n).toLocaleString()}`;
}

/**
 * Estimate precedence — ONE rule for every cost surface:
 *   1. phase-hour total (lifecycle card) — most specific, a point estimate
 *   2. est_hours                         — a point estimate
 *   3. t-shirt size                      — a range
 * Returns null when the project carries none of them.
 */
export type CostEstimate =
  | { kind: "point"; hours: number; source: "est_hours" }
  | { kind: "range"; bucket: TshirtBucket; source: "tshirt" };

export function costEstimateFor(p: {
  phase_hours_total?: number | null;
  est_hours?: number | null;
  tshirt_size?: string | null;
}): CostEstimate | null {
  // Lifecycle card + phase hours retired 2026-10-12 (Brett: milestones
  // cover it). phase_hours_total is ignored even where rows still exist.
  if (p.est_hours != null && p.est_hours > 0) {
    return { kind: "point", hours: p.est_hours, source: "est_hours" };
  }
  const bucket = tshirtBucket(p.tshirt_size);
  return bucket ? { kind: "range", bucket, source: "tshirt" } : null;
}

/** "$13,000" for a point estimate, the range label otherwise, "—" if none. */
export function costLabelFor(
  p: { phase_hours_total?: number | null; est_hours?: number | null; tshirt_size?: string | null },
  rate: number,
): string {
  const est = costEstimateFor(p);
  if (!est || !rate || rate <= 0) return "—";
  if (est.kind === "point") return fmtMoneyExact(est.hours * rate);
  return costRangeLabel(p.tshirt_size, rate);
}
